import { rd } from '@passionware/monads';
import { X } from 'lucide-react';
import { cloneElement, type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { ResourceType } from '__types__';
import { Button } from 'v5/design-system/Button';
import { focusRings } from 'v5/design-system/_common/focusRing';
import { cn } from 'v5/platform/dom/cn';
import { FieldContextProvider } from 'v5/patterns/forms/schema-form/FieldContext';
import { AsyncSelector } from 'v5/patterns/forms/schema-form/fields/AsyncSelector';
import {
  createResourceReferenceSource,
  type ResourceReferenceFilters,
  type ResourceReferenceScope,
  type ResourceReferenceServices
} from 'v5/patterns/resource/resourceReferenceSource';
import { EntityHoverCard } from 'v5/patterns/widgets/EntityHoverCard';
import { renderError } from 'v5/patterns/widgets/renderError';
import { Skeleton } from 'v5/design-system/Skeleton';

export type ResourceComboboxProps = ResourceReferenceServices & {
  /** The picked resource's id; `null` shows the search. */
  value: number | null;
  onChange(id: number | null): void;
  /** Which resources are offered (their types), on top of the browsing scope. */
  filters?: ResourceReferenceFilters;
  /** Whose resources are offered; default: the browsing scope's. */
  scope?: ResourceReferenceScope;
  /** Offers “Create” for the typed name into the first type this accepts; absent: no create. */
  isCreatable?(type: ResourceType): boolean;
  /** A button that opens the search, for hosts that only fill from a resource and show nothing picked. */
  trigger?: ReactElement;
  /** Focus the search trigger on mount (a modal whose first step is picking). */
  autoFocus?: boolean;
  disabled?: boolean;
};

/**
 * One resource in the browsing scope: a search combobox while empty, the
 * picked resource (name opens it, hovering shows its card) with a clear
 * button once chosen.
 */
export function ResourceCombobox({
  services,
  value,
  onChange,
  filters = {},
  scope,
  isCreatable,
  trigger,
  autoFocus = false,
  disabled
}: ResourceComboboxProps) {
  const { t } = useTranslation('patterns');
  const appScope = services.appScopeService.useSlugAppScope();
  const source = useMemo(
    () => createResourceReferenceSource(services, () => appScope),
    [services, appScope]
  );
  const create = source.useCreate(isCreatable ?? (() => false));

  if (value !== null && trigger === undefined) {
    return (
      <PickedResource
        source={source}
        id={value}
        disabled={disabled}
        onClear={() => onChange(null)}
        clearLabel={t('SchemaForm.reference.clear')}
      />
    );
  }

  return (
    <FieldContextProvider
      value={{
        mode: disabled ? 'display' : 'edit',
        variant: 'regular',
        size: 'md',
        basePath: '',
        ghost: false
      }}
    >
      <AsyncSelector<number, number>
        source={{
          useOption: source.useOption,
          useOptions: query => source.useOptions(query, filters, scope),
          optionValueToFormValue: id => id,
          optionValueToKey: id => String(id)
        }}
        value={null}
        onChange={onChange}
        onBlur={() => undefined}
        name="resource"
        triggerRef={null}
        placeholder={t('SchemaForm.reference.select', { noun: source.noun })}
        searchPrompt={t('SchemaForm.reference.search', { noun: source.noun })}
        emptyContent={null}
        appearance={{ type: 'standalone' }}
        autoFocus={autoFocus}
        create={create}
        trigger={
          trigger && disabled
            ? cloneElement(trigger, { disabled: true })
            : trigger
        }
      />
    </FieldContextProvider>
  );
}

function PickedResource({
  source,
  id,
  disabled,
  onClear,
  clearLabel
}: {
  source: ReturnType<typeof createResourceReferenceSource>;
  id: number;
  disabled?: boolean;
  onClear(): void;
  clearLabel: string;
}) {
  const card = source.useCard(id);
  const pageHref = source.usePageHref(id);
  const name = rd
    .fullJourney(rd.useLastWithPlaceholder(source.useOption(id)))
    .initially(null)
    .wait(<Skeleton className="h-4 w-32" />)
    .catch(renderError)
    .map(label => label);
  const onOpen = () => source.open(id);
  return (
    <div className="flex items-center gap-1">
      <EntityHoverCard card={card} onOpen={onOpen} pageHref={pageHref}>
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'min-w-0 flex-1 rounded-sm py-1 text-left',
            focusRings.focus.visible
          )}
        >
          {name}
        </button>
      </EntityHoverCard>
      {!disabled && (
        <Button
          type="button"
          variant="ghost"
          size="xs"
          layout="icon"
          aria-label={clearLabel}
          onClick={onClear}
        >
          <X />
        </Button>
      )}
    </div>
  );
}
